import { Moon, Sun } from 'lucide-react';
import hotkeys from 'hotkeys-js';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../../../apps/frontend/src/context/ThemeStore';
import axios from 'axios';
import ShortcutsModal from '../components/modals/ShortcutsModal';

export const DarkModeToggle = ({ sidebar = false, style = {} }) => {
  const { user, isDarkTheme, setIsDarkTheme } = useContext(ThemeContext);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const fireConfetti = () => {
    if (typeof window.confetti !== 'function') return;

    const count = 500;
    const defaults = {
      origin: { y: 0.5, x: 0.5 },
    };

    function fire(particleRatio, opts) {
      window.confetti(
        Object.assign({}, defaults, opts, {
          particleCount: Math.floor(count * particleRatio),
        }),
      );
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.4 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  };

  const handleThemeChange = useCallback(async () => {
    const newIsDarkThemeValue = !isDarkTheme;

    if (user) {
      try {
        const response = await axios.patch(`/api/user/profile/settings`, {
          isDarkTheme: newIsDarkThemeValue,
        });
        localStorage.setItem('userProfile', JSON.stringify(response?.data?.data));
      } catch (error) {
        console.error('DarkModeToggle: Failed to update theme setting on backend:', error);
      }
    }

    setIsDarkTheme(newIsDarkThemeValue);
    const root = window.document.documentElement;
    root.classList.remove('dark', 'light');
    root.classList.add(newIsDarkThemeValue ? 'dark' : 'light');
    // fireConfetti();
  }, [isDarkTheme, user]);

  useEffect(() => {
    const existingScript = document.getElementById('canvas-confetti-cdn');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'canvas-confetti-cdn';
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
      script.async = true;
      document.body.appendChild(script);
    }

    const shortcut = 'shift+d';
    const keyboardShortcutsListShortcut = 'ctrl+/,cmd+/';
    const handler = (event, hotkey) => {
      event.preventDefault();
      handleThemeChange();
    };

    const keyboardShortcutsListHandler = (event, hotkey) => {
      event.preventDefault();
      setShowShortcuts((prev) => !prev);
    };

    hotkeys(shortcut, handler);
    hotkeys(keyboardShortcutsListShortcut, keyboardShortcutsListHandler);

    return () => {
      hotkeys.unbind(shortcut, handler);
      hotkeys.unbind(keyboardShortcutsListShortcut, keyboardShortcutsListHandler);
    };
  }, [handleThemeChange]);

  return (
    <div
      style={{
        position: !sidebar ? 'fixed' : style.position || 'relative',
        right: !sidebar ? '30px' : style.right || 'auto',
        bottom: !sidebar ? '80px' : style.bottom || 'auto',
        zIndex: !sidebar ? 11 : style.zIndex || 'auto',
        transform: style.transform || 'none',
        marginRight: style.marginRight || '0',
        ...style, // to spread any additional style props
      }}
    >
      <div className="relative group inline-flex h-10 w-10 min-w-fit rounded-full p-[2px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 cursor-pointer">
        <span className="absolute inset-0 rounded-full bg-[conic-gradient(from_90deg_at_50%_50%,var(--q3-accent-normal)_0%,var(--q3-danger-light)_50%,var(--q3-accent-normal)_100%)] opacity-0 group-hover:opacity-100 group-hover:animate-[spin_2s_linear_infinite] transition-opacity" />
        <button
          onClick={handleThemeChange}
          aria-label="Toggle theme"
          className="relative z-10 inline-flex h-full w-full items-center justify-center rounded-full bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors backdrop-blur-3xl cursor-pointer"
        >
          {isDarkTheme ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>
      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} show={showShortcuts} />}
    </div>
  );
};
